import { useMemo } from 'react';
import { CLIENT_NOTES_CHANGED_EVENT, readClientNotes, updateClientNotes, useClientNotes } from './clientNotesStore';
import type { ClientNotes } from './clientNotesStore';

/**
 * 招募 IDEA's 痛點 / 顧慮 / 優勢 — what the candidate SAID, picked by the
 * recruiter as chips. The app, not the recruiter, supplies what to answer:
 * each pain point carries a 賣點 and each concern a 化解 (design doc §6.1 /
 * §6.2, docs/specs/2026-08-31-clientv2-recruitment-mode-design.md).
 *
 * At most three of each (🔒 §7): three is what fits in the three minutes
 * before a meeting, and a longer list stops being a priority.
 *
 * Stored on the server as `factsheet.recruitment.painPoints / concerns / strengths` (§4.1), through ./clientNotesStore; the key lists above stay web-only (§5.1).
 */

export const RECRUIT_PROFILE_CAP = 3;

// §6.1, in the table's order (value families: 時間自由 · 收入潛力 · 自己生意 ·
// 建立團隊 · 憑實力 · 行業前景). Message ids: clientv2-recruit-pain-<key>[-sell].
export const RECRUIT_PAIN_POINTS = [
  'time',
  'income',
  'retire',
  'capital',
  'layoff',
  'promotion',
  'politics',
  'recognition',
  'boring',
  'economy',
  'sunset',
  'fulfilment'
] as const;

// §6.2. Message ids: clientv2-recruit-concern-<key>[-answer].
export const RECRUIT_CONCERNS = [
  'unstable',
  'noClients',
  'rejection',
  'family',
  'image',
  'seniority',
  'notSales',
  'exam',
  'attrition',
  'stableJob'
] as const;

// No table in the doc — a starter set. Message ids: clientv2-recruit-strength-<key>.
export const RECRUIT_STRENGTHS = [
  'network',
  'patient',
  'communicator',
  'disciplined',
  'salesExp',
  'learner',
  'driven',
  'warm',
  'resilient',
  'finance'
] as const;

export type RecruitProfileKind = 'painPoints' | 'concerns' | 'strengths';

export const RECRUIT_PROFILE_OPTIONS: Record<RecruitProfileKind, readonly string[]> = {
  painPoints: RECRUIT_PAIN_POINTS,
  concerns: RECRUIT_CONCERNS,
  strengths: RECRUIT_STRENGTHS
};

export type RecruitProfile = Record<RecruitProfileKind, string[]>;

export const EMPTY_RECRUIT_PROFILE: RecruitProfile = { painPoints: [], concerns: [], strengths: [] };

const KINDS = Object.keys(RECRUIT_PROFILE_OPTIONS) as RecruitProfileKind[];

/** Known keys only, first-picked order, capped — whatever storage handed back. */
const clean = (raw: unknown): RecruitProfile => {
  const source = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const profile = { ...EMPTY_RECRUIT_PROFILE };
  KINDS.forEach(kind => {
    const list = Array.isArray(source[kind]) ? (source[kind] as unknown[]) : [];
    const allowed = RECRUIT_PROFILE_OPTIONS[kind];
    profile[kind] = list
      .filter(
        (key, index): key is string => typeof key === 'string' && allowed.includes(key) && list.indexOf(key) === index
      )
      .slice(0, RECRUIT_PROFILE_CAP);
  });
  return profile;
};

/** Adds or removes one chip. Adding past the cap does nothing. */
export const toggleRecruitProfile = (
  profile: RecruitProfile,
  kind: RecruitProfileKind,
  key: string
): RecruitProfile => {
  const current = profile[kind];
  if (current.includes(key)) return { ...profile, [kind]: current.filter(k => k !== key) };
  if (current.length >= RECRUIT_PROFILE_CAP) return profile;
  return { ...profile, [kind]: [...current, key] };
};

// Kept for the list-level reader: 今日招募 reads these chips across every candidate.
export const RECRUIT_PROFILE_CHANGED_EVENT = CLIENT_NOTES_CHANGED_EVENT;

const profileOf = (notes: ClientNotes | null): RecruitProfile => clean(notes ? notes.recruitment : {});

export const readRecruitProfile = (clientId: string): RecruitProfile => profileOf(readClientNotes(clientId));

export const writeRecruitProfile = (clientId: string, profile: RecruitProfile) => {
  const next = clean(profile);
  void updateClientNotes(
    clientId,
    { recruitment: { painPoints: next.painPoints, concerns: next.concerns, strengths: next.strengths } },
    notes => ({ ...notes, recruitment: { ...notes.recruitment, ...next } })
  );
};

/** One client's picks, kept current across the 快速資料 sheet and 招募 IDEA. */
export const useRecruitProfile = (clientId: string | null | undefined) => {
  const notes = useClientNotes(clientId);
  return useMemo(() => profileOf(notes), [notes]);
};
